import React,{useCallback,useContext,useState,useEffect,useRef,useMemo} from 'react'
import {Outlet} from 'react-router-dom'
import {ReactFlowProvider} from 'reactflow'
import Navbar from '../../components/Nav/Navbar'
import {CreditsProvider} from '../../contexts/CreditsContext'
import {ProjectsProvider} from '../../contexts/ProjectsContext'
import {getCredits} from '../../helpers/functions'
import UserContext from '../../contexts/UserContext'
import ErrorContext from '../../contexts/ErrorContext'
import {getDocs,collection,orderBy} from 'firebase/firestore'
import {db,rt,storage} from '../../helpers/firebase'
import {getDownloadURL,ref} from 'firebase/storage'
import FeedbackCard from '../../components/Cards/FeedbackCard'
import {ref as rtRef,get,query,equalTo,orderByChild,child,onValue,push} from 'firebase/database'
import {useToast} from '../../contexts/ToastContext'
import PLAN_LIMITS from '../../config/planLimits'
import useUserSubscription from '../../hooks/useUserSubscription'

export default function Main() {

    const setError=useContext(ErrorContext)
    const {showWarning}=useToast()
    const {getUserPlan}=useUserSubscription()

    const [user]=useContext(UserContext)

    const navRef=useRef(null)

    const [credits,setCredits]=useState(0)
    const [loading,setLoading]=useState(true)
    const [projects,setProjects]=useState([])
    const [folders,setFolders]=useState([])



    const updateCredits=useCallback(async () => {
        try {
            const response=await getCredits()
            const freshCredits=response.data.credits||0
            setCredits(freshCredits)
            // Warn at 20% of monthly allocation
            if(freshCredits>0&&freshCredits!==-1) {
                const plan=await getUserPlan()
                const limits=PLAN_LIMITS[plan==='premium'? 'premium':plan==='plus'? 'plus':'free']
                const monthlyCredits=limits?.monthlyCredits
                if(monthlyCredits&&monthlyCredits!==Infinity&&freshCredits/monthlyCredits<=0.2) {
                    showWarning(`You have ${freshCredits} AI credit${freshCredits!==1?'s':''} remaining (under 20%). Upgrade to keep building.`)
                }
            }
            return freshCredits
        } catch(err) {
            window.location.reload()
        }
    },[getUserPlan,showWarning])

    const sortedProjects=useMemo(() => {
        return projects.sort((a,b) => b.updatedAt-a.updatedAt)
    },[projects])


    const updateProjects=useCallback(() => {
        const userRef=rtRef(rt,'projects/'+user.uid)
        get(userRef).then(projectsSnapshot => {
            let projectsResponse=projectsSnapshot.val()
            projectsResponse=projectsResponse? Object.keys(projectsResponse).map((key) => {
                const projectRef=ref(storage,`users/${user.uid}/${key}/view.png`)
                return {
                    id: key,
                    projectRef: projectRef,
                    link: key,
                    ownerId: user.uid,
                    ...projectsResponse[key]
                }
            }):[]
            if(projectsResponse?.length===0) {
                setLoading(false)
                return
            }
            setProjects(projectsResponse)
            setLoading(false)
        })
        // Find all projects where the user's email is in the collaborators list project.collaborators.child.email === user.email
        const collabRef=rtRef(rt,'collaboratorsMap/'+encodeURIComponent(user.email).replaceAll('.','%2E'))
        get(collabRef).then(collabSnapshot => {
            let collabResponse=collabSnapshot.val()
            if(collabResponse) {
                Object.keys(collabResponse).forEach((projectId) => {
                    const projectRtRef=rtRef(rt,'projects/'+collabResponse[projectId].ownerId+'/'+projectId)
                    get(projectRtRef,'value').then(projectSnapshot => {
                        const project=projectSnapshot.val()
                        const storageRef=ref(storage,`/users/${collabResponse[projectId].ownerId}/${projectId}/view.png`)
                        setProjects((prevProjects) => {
                            return [...prevProjects.filter((prevProject) => prevProject.id!==projectId),{
                                id: projectId,
                                projectRef: storageRef,
                                link: `${collabResponse[projectId].ownerId}/${projectId}`,
                                ownerId: collabResponse[projectId].ownerId,
                                ...collabResponse[projectId],
                                ...project
                            }].sort((a,b) => {
                                return a.updatedAt-b.updatedAt
                            })
                        })
                    })
                })
            }
        })

        const foldersRef=rtRef(rt,'folders/'+user.uid)
        get(foldersRef).then(foldersSnapshot => {
            let foldersResponse=foldersSnapshot.val()
            if(foldersResponse) {
                setFolders(Object.keys(foldersResponse).map((key) => {
                    return {
                        id: key,
                        ...foldersResponse[key]
                    }
                }))
            }
        })
    },[user?.uid])

    useEffect(() => {
        updateProjects()
        updateCredits()
    },[updateCredits,updateProjects])

    // Ref to allow child routes (Design.js) to register a version-history toggle callback
    const versionHistoryCallbackRef=useRef(null)

    return (
        <div className='w-full min-h-screen'>
            <CreditsProvider value={[credits,updateCredits]}>
                <ProjectsProvider value={[sortedProjects,updateProjects,folders]}>
                    <ReactFlowProvider>
                        <Navbar ref={navRef} onVersionHistory={()=>versionHistoryCallbackRef.current?.()} />
                        <Outlet context={{navRef,versionHistoryCallbackRef}} />
                        <FeedbackCard />
                    </ReactFlowProvider>
                </ProjectsProvider>
            </CreditsProvider>
        </div>
    )
}
